import * as canvasLib from "./canvas.js";
import { MaxPersistenceMs, persistenceFromMs } from "./canvas.js";
import { FakeAtomVideo, FakeVideo, Video } from "../video.js";
import { LineGridRows } from "../video-filters/pixel-grid.js";
import { toast } from "./toast.js";

// While running fast the state machines still run accurately, but painting is
// the most expensive part of jsbeeb, so most frames are skipped. Odd, so that
// interlaced modes (MODE 7) alternate fields across the frames that do paint.
const SpeedyFrameSkip = 9;

// More fields of decay than this leaves nothing at any setting: at the longest
// afterglow a field keeps 96%, and this many take that below a level.
const MaxDecayFields = 250;

/**
 * The picture: the canvas and its filter, the video chip that paints into a
 * framebuffer of our own, and the animation frame that presents it. A stalled
 * display holds up the picture and not the emulation (issue #885).
 */
export class Display {
    constructor({
        screenCanvas,
        model,
        mode,
        tryGl = true,
        lowLatency = true,
        fakeVideo = false,
        hasVideoNula = false,
        onMissingVideoNula = null,
        frameSkip = 0,
        makeCanvas = (canvasEl, filterClass) =>
            tryGl
                ? canvasLib.bestCanvas(canvasEl, filterClass, lowLatency)
                : new canvasLib.Canvas(canvasEl, lowLatency),
    }) {
        this.screenCanvas = screenCanvas;
        this.persistence = {};
        this.fieldMs = 1000 / (model.isAtom ? 60 : 50);
        this.frames = 0;
        this.frameSkip = frameSkip;
        this.paintMsThisTick = 0;
        this.presentMsMax = 0;
        this.presentScheduled = false;
        this.presented = true;

        this.filterClass = canvasLib.getFilterForMode(mode);
        // Each mode says how many pixels it wants to draw into. Set this before
        // creating the context, which fixes its initial viewport.
        this.sizeCanvasFor(this.filterClass);
        this.canvas = makeCanvas(screenCanvas, this.filterClass);
        this.reportAnyFallback(this.filterClass);
        this.filterClass = this.canvas.filterClass;

        // The animation frame asked for at flyback covers paints while the loop
        // is stopped; while it runs, its vsync tick presents.
        this.pendingFrame = {
            minx: 0,
            miny: 0,
            maxx: 0,
            maxy: 0,
            lineBaseEven: 0,
            lineBaseOdd: 0,
            phaseBaseEven: 0,
            phaseBaseOdd: 0,
            lineGrid: new Uint8Array(LineGridRows),
            fields: 1,
        };
        this.lastPaintedFrameCount = 0;
        this.fieldsSincePresent = 0;

        const display = this;
        this.video = fakeVideo
            ? model.isAtom
                ? new FakeAtomVideo()
                : new FakeVideo()
            : new Video(
                  model.isMaster,
                  this.canvas.fb32.length,
                  function paint(minx, miny, maxx, maxy, paintedTo) {
                      display.onPaint(this, minx, miny, maxx, maxy, paintedTo);
                  },
                  { isAtom: model.isAtom, hasVideoNula, onMissingVideoNula },
              );

        this.setCrtPic();
    }

    /**
     * While running fast the skip moves into the video chip, which skips the
     * render as well as the present; the deeper of the chip's skip and any
     * configured frameSkip wins, so the two never multiply together.
     */
    setSpeedy(speedy) {
        const skip = Math.max(SpeedyFrameSkip, this.frameSkip);
        this.video.frameSkipCount = speedy ? (skip % 2 ? skip : skip + 1) : 0;
    }

    /**
     * `paintedTo` is the row the copy stops at; the rows below it keep what the
     * canvas last showed. A paint that gives one is the debugger's and is never
     * skipped.
     */
    onPaint(video, minx, miny, maxx, maxy, paintedTo) {
        // The debugger's paint replaces the picture: its beam dot would otherwise
        // outshine the black it moved off and stay, every step leaving another.
        const replacesPicture = video.paintsAfresh || paintedTo !== undefined;
        if (paintedTo === undefined) {
            paintedTo = maxy;
            if (!video.frameSkipCount) {
                this.frames++;
                if (this.frames < this.frameSkip) return;
                this.frames = 0;
            }
        }
        const start = performance.now();
        this.canvas.fb32.set(video.fb32.subarray(miny * 1024, paintedTo * 1024), miny * 1024);
        this.pendingFrame.lineGrid.set(video.lineGrid.subarray(miny, paintedTo), miny);
        Object.assign(this.pendingFrame, {
            minx,
            miny,
            maxx,
            maxy,
            lineBaseEven: video.lineBaseEven,
            lineBaseOdd: video.lineBaseOdd,
            phaseBaseEven: video.phaseBaseEven,
            phaseBaseOdd: video.phaseBaseOdd,
        });
        // How many fields the phosphor has decayed over since the frame the
        // canvas shows: one usually, more under a frame skip or when paints
        // outrun the animation frame, none for a repaint of the same frame, and
        // all of them after a restore, whose picture owes the old one nothing.
        // The 6847 keeps its own count and syncs it after painting.
        const frameCount = (video.video6847 ?? video).frameCount;
        const elapsed = Math.max(0, frameCount - this.lastPaintedFrameCount);
        this.fieldsSincePresent = replacesPicture
            ? MaxDecayFields
            : Math.min(MaxDecayFields, this.fieldsSincePresent + elapsed);
        video.paintsAfresh = false;
        this.pendingFrame.fields = this.fieldsSincePresent;
        this.lastPaintedFrameCount = frameCount;
        this.paintMsThisTick += performance.now() - start;
        this.presented = false;
        if (!this.presentScheduled) {
            this.presentScheduled = true;
            window.requestAnimationFrame(() => {
                this.presentScheduled = false;
                this.present();
            });
        }
    }

    present() {
        if (this.presented) return;
        this.presented = true;
        const start = performance.now();
        const { minx, miny, maxx, maxy } = this.pendingFrame;
        this.canvas.paint(minx, miny, maxx, maxy, this.pendingFrame);
        this.fieldsSincePresent = 0;
        this.presentMsMax = Math.max(this.presentMsMax, performance.now() - start);
    }

    /**
     * Sets a display's afterglow time in milliseconds, by the name of its
     * setting; it applies while the filter in use is the one that declares that
     * setting, so a fallback gets its own amount, not the amount of the mode
     * that was asked for.
     */
    setPersistence(setting, afterglowMs) {
        this.persistence[setting] = Number.isFinite(afterglowMs)
            ? persistenceFromMs(Math.min(MaxPersistenceMs, Math.max(0, afterglowMs)), this.fieldMs)
            : 0;
        if (setting === this.persistenceSetting()) this.applyPersistence();
    }

    /** The setting that governs the display in use, or nothing where the canvas cannot keep a picture. */
    persistenceSetting() {
        return this.canvas.canPersist ? this.filterClass.getDisplayConfig().persistence?.setting : undefined;
    }

    applyPersistence() {
        const setting = this.persistenceSetting();
        this.canvas.setPersistence(setting === undefined ? 0 : (this.persistence[setting] ?? 0));
    }

    /** The mode is changed from a modal, which stops the emulator, so this repaints itself. */
    setMode(mode) {
        const newFilterClass = canvasLib.getFilterForMode(mode);
        // Everything but the filter is the same whatever the mode: the framebuffer
        // texture, the vertex buffers and fb32 all carry over untouched.
        canvasLib.useBestFilter(this.canvas, newFilterClass);
        this.reportAnyFallback(newFilterClass);
        // Follow the filter we ended up with, not the one we asked for: everything
        // downstream (the monitor picture, the canvas geometry, how large a drawing
        // buffer to ask for) comes from its display config.
        this.filterClass = this.canvas.filterClass;
        // Back to the mode's own size, undoing any scaling the last one asked for.
        this.sizeCanvasFor(this.filterClass);
        this.applyPersistence();
        // The new mode's picture replaces the old one's, not glows through it.
        this.fieldsSincePresent = MaxDecayFields;
        this.video.paint();
        this.setCrtPic();
    }

    sizeCanvasFor(filterClass) {
        const displayConfig = filterClass.getDisplayConfig();
        if (
            this.screenCanvas.width === displayConfig.canvasWidth &&
            this.screenCanvas.height === displayConfig.canvasHeight
        )
            return;
        this.screenCanvas.width = displayConfig.canvasWidth;
        this.screenCanvas.height = displayConfig.canvasHeight;
    }

    // Test which filter is actually in use, not merely whether we got WebGL: a
    // filter can decline a context that works perfectly well for other modes, in
    // which case we are quietly left with an unfiltered display.
    reportAnyFallback(filterClass) {
        if (this.canvas.filterClass === filterClass) return;
        const reason = this.canvas.fallbackReason ? ` (${this.canvas.fallbackReason})` : "";
        const { name } = filterClass.getDisplayConfig();
        toast(`${name} is not available on this device, so the standard display is in use${reason}.`, {
            title: "Display",
            quietKey: "quietDisplayFallback",
        });
    }

    /** The monitor picture around the screen follows the filter in use. */
    setCrtPic() {
        const config = this.filterClass.getDisplayConfig();
        const monitorPic = document.getElementById("cub-monitor-pic");
        monitorPic.src = config.image;
        monitorPic.alt = config.imageAlt;
        monitorPic.width = config.imageWidth;
        monitorPic.height = config.imageHeight;
    }

    /** This tick's time spent copying frames out of the emulator, and start counting afresh. */
    takePaintMs() {
        const ms = this.paintMsThisTick;
        this.paintMsThisTick = 0;
        return ms;
    }

    /** The longest present since last asked, and start counting afresh. */
    takePresentMs() {
        const ms = this.presentMsMax;
        this.presentMsMax = 0;
        return ms;
    }
}
